import { useLiveQuery } from "dexie-react-hooks";
import { Search as SearchIcon, X } from "lucide-react";
import { useSearchParams } from "react-router";
import { sortTasks, TaskList } from "../components/TaskList.js";
import { EmptyState, IconButton, PageHeader, SectionHeader } from "../components/ui.js";
import { db } from "../lib/db.js";

/** Full-text search across task names, descriptions and comments. */
export function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const needle = q.trim().toLowerCase();
  const results = useLiveQuery(async () => {
    if (!needle) return { open: [], done: [] };
    const commentHits = new Set((await db.comments.filter((c) => c.content.toLowerCase().includes(needle)).toArray()).map((c) => c.taskId));
    const hits = await db.tasks.filter((t) => t.content.toLowerCase().includes(needle) || t.description.toLowerCase().includes(needle) || commentHits.has(t.id)).toArray();
    return { open: sortTasks(hits.filter((t) => !t.completedAt)), done: hits.filter((t) => t.completedAt).sort((a, b) => b.completedAt!.localeCompare(a.completedAt!)) };
  }, [needle]);
  const set = (v: string) => setParams((p) => { if (v) p.set("q", v); else p.delete("q"); return p; }, { replace: true });

  return (
    <div>
      <PageHeader title="Search" />
      <div className="relative mb-6">
        <SearchIcon size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-faint" aria-hidden />
        <input autoFocus type="search" value={q} placeholder="Tasks, descriptions and comments" aria-label="Search"
          onChange={(e) => set(e.target.value)}
          className="h-12 w-full rounded-card border border-border bg-surface-raised pl-11 pr-12 text-heading outline-none placeholder:text-text-faint hover:border-border-strong focus:border-brand focus:ring-2 focus:ring-brand/20 [&::-webkit-search-cancel-button]:hidden" />
        {q && <IconButton icon={X} label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2" onClick={() => set("")} />}
      </div>
      {!needle && <EmptyState compact kind="search" title="Find anything">Search task names, descriptions and comments. Press Ctrl K anywhere to jump straight to a task or project.</EmptyState>}
      {needle && results && !results.open.length && !results.done.length && <EmptyState compact kind="search" title={`Nothing matches “${q}”`}>Try fewer or different words.</EmptyState>}
      {!!results?.open.length && (
        <section className="mb-8"><SectionHeader title="Open" count={results.open.length} /><TaskList tasks={results.open} showProject addButton={false} /></section>
      )}
      {!!results?.done.length && (
        <section><SectionHeader title="Completed" count={results.done.length} /><TaskList tasks={results.done} showProject hideOnComplete={false} addButton={false} /></section>
      )}
    </div>
  );
}
